import '../App.css'
import {
    Center, Text, Container, Space, SimpleGrid, Divider,
    List, Card, Badge, createStyles, rem, Stack, Box, Group
} from '@mantine/core'
import Footer from '../components/Footer';
import Head from '../components/Head'
import { IconCrown, IconStar } from '@tabler/icons-react';

// ── Colour palette for award categories ─────────────────────────────────────
const COLORS = {
    globalComic: '#532d86',   // purple
    regional: '#2b9eb3',   // teal
    fan: '#1a9e2e',   // green
    mic: '#1a9e2e',   // green
    gold: '#c8960c',   // gold (Pobblebonk)
}

const useStyles = createStyles((theme) => ({

    // ── Hero header ─────────────────────────────────────────────────────────
    heroWrapper: {
        background: 'radial-gradient(ellipse at 50% 60%, #ede0ff 0%, #e8f6fb 50%, #f4fbff 100%)',
        paddingTop: rem(32),
        paddingBottom: rem(32),
        textAlign: 'center',
    },

    pageTitle: {
        fontFamily: "'Merriweather', 'Georgia', serif",
        fontWeight: 900,
        letterSpacing: '-0.01em',
        fontSize: rem(72),
        color: '#5c0099',

        [theme.fn.smallerThan('md')]: { fontSize: rem(52) },
        [theme.fn.smallerThan('sm')]: { fontSize: rem(38) },
    },

    sectionTitle: {
        fontFamily: "'Merriweather', 'Georgia', serif",
        fontWeight: 800,
        fontSize: rem(38),
        textAlign: 'center',
        color: theme.colors.gray[8],

        [theme.fn.smallerThan('md')]: { fontSize: rem(30) },
        [theme.fn.smallerThan('sm')]: { fontSize: rem(24) },
    },

    // ── Year banner ─────────────────────────────────────────────────────────
    yearBanner: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: rem(12),
        margin: `${rem(36)} 0 ${rem(20)}`,
    },

    yearPill: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 800,
        fontSize: rem(22),
        background: 'linear-gradient(135deg, #532d86 0%, #7b00b8 100%)',
        color: '#fff',
        borderRadius: rem(50),
        padding: `${rem(6)} ${rem(28)}`,
        letterSpacing: '0.04em',
        display: 'inline-block',

        [theme.fn.smallerThan('sm')]: { fontSize: rem(17), padding: `${rem(5)} ${rem(18)}` },
    },

    yearSubtitle: {
        fontSize: rem(14),
        color: theme.colors.gray[5],
        fontStyle: 'italic',
        textAlign: 'center',
        marginTop: rem(-12),
        marginBottom: rem(12),
    },

    // ── Award card ──────────────────────────────────────────────────────────
    awardCard: {
        borderLeft: `4px solid transparent`,
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        background: 'rgba(255,255,255,0.75)',
        backdropFilter: 'blur(4px)',

        '&:hover': {
            boxShadow: theme.shadows.md,
            transform: 'translateY(-2px)',
        },
    },

    awardCategory: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 700,
        fontSize: rem(13),
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        marginBottom: rem(4),
    },

    winner: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 700,
        fontSize: rem(18),

        [theme.fn.smallerThan('sm')]: { fontSize: rem(16) },
    },

    runnerUp: {
        fontSize: rem(13),
        color: theme.colors.gray[6],
        marginTop: rem(2),
    },

    // ── Divider label ────────────────────────────────────────────────────────
    dividerLabel: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 700,
        fontSize: rem(12),
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: theme.colors.gray[5],
    },

    sectionDivider: {
        margin: `${rem(36)} 0 ${rem(28)}`,
    },

    // ── Pobblebonk gold card ─────────────────────────────────────────────────
    pobblebonkCard: {
        background: 'linear-gradient(135deg, #fffbea 0%, #fff3c4 100%)',
        border: `2px solid #f5c842`,
        borderRadius: theme.radius.lg,
        padding: rem(32),
        maxWidth: rem(600),
        margin: '0 auto',
        textAlign: 'center',
        boxShadow: '0 4px 24px rgba(200,150,12,0.18)',
    },

    pobblebonkTitle: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 800,
        fontSize: rem(30),
        color: '#7a5800',

        [theme.fn.smallerThan('sm')]: { fontSize: rem(22) },
    },

    pobblebonkWinner: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 700,
        fontSize: rem(22),
        color: '#a06a00',

        [theme.fn.smallerThan('sm')]: { fontSize: rem(18) },
    },
}))

// ── Helper: an individual award entry card ───────────────────────────────────
interface AwardEntryProps {
    category: string
    borderColor: string
    iconColor: string
    iconSize?: number
    first: string
    second?: string
    third?: string
}

function AwardEntry({ category, borderColor, iconColor, iconSize = 28, first, second, third }: AwardEntryProps) {
    const { classes } = useStyles()
    return (
        <Card
            className={classes.awardCard}
            shadow="sm"
            padding="md"
            radius="md"
            withBorder
            sx={{ borderLeftColor: borderColor }}
        >
            <Stack spacing={4}>
                {/* Category label + icon */}
                <div style={{ display: 'flex', alignItems: 'center', gap: rem(8) }}>
                    <IconCrown size={iconSize} strokeWidth={2} color={iconColor} style={{ flexShrink: 0 }} />
                    <Text className={classes.awardCategory} sx={{ color: iconColor }}>
                        {category}
                    </Text>
                </div>

                {/* 1st place — prominent */}
                <Text className={classes.winner}>{first}</Text>

                {/* 2nd & 3rd — muted */}
                {second && <Text className={classes.runnerUp}>2nd: {second}</Text>}
                {third && <Text className={classes.runnerUp}>3rd: {third}</Text>}
            </Stack>
        </Card>
    )
}

// ── Helper: year banner ──────────────────────────────────────────────────────
function YearBanner({ year, subtitle }: { year: string; subtitle?: string }) {
    const { classes } = useStyles()
    return (
        <>
            <div className={classes.yearBanner}>
                <Divider sx={{ flex: 1 }} />
                <span className={classes.yearPill}>{year}</span>
                <Divider sx={{ flex: 1 }} />
            </div>
            {subtitle && <Text className={classes.yearSubtitle}>{subtitle}</Text>}
        </>
    )
}

// ── Main component ────────────────────────────────────────────────────────────
function HOF() {
    const { classes } = useStyles()

    const links = [
        { "link": "/About", "label": "About" },
        { "link": "/Support", "label": "Support" },
        { "link": "/Pp", "label": "Privacy" },
        { "link": "/Tos", "label": "Terms of Use" },
    ]

    return (
        <body>
            <Head />

            {/* ── Hero header ── */}
            <div className={classes.heroWrapper}>
                <div style={{ display: 'flex', justifyContent: 'center', gap: rem(12), alignItems: 'center', marginBottom: rem(8) }}>
                    <IconStar size={32} color="#c8960c" />
                    <IconStar size={48} color="#c8960c" />
                    <IconStar size={32} color="#c8960c" />
                </div>
                <Text className={`HOF ${classes.pageTitle}`}>Hall Of Fame</Text>
                <Group position="center" mt={4}>
                    <Badge color="violet" variant="filled" size="md" radius="sm">
                        Celebrating Comedy Since 2022
                    </Badge>
                </Group>
                <Text size="lg" color="dimmed" mt={8}>Celebrating the best of the live stand-up comedy scene</Text>
            </div>

            {/* ── Divider: Hero → Pobblebonk ── */}
            <Divider
                className={classes.sectionDivider}
                label={<span className={classes.dividerLabel}>Highest honour</span>}
                labelPosition="center"
                size="xs"
            />

            {/* ── Pobblebonk Award spotlight ── */}
            <Container>
                <div className={classes.pobblebonkCard}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: rem(12) }}>
                        <IconCrown size={52} strokeWidth={1.5} color={COLORS.gold} />
                    </div>
                    <Text className={classes.pobblebonkTitle}>🏆 Pobblebonk Award</Text>
                    <Space h="xs" />
                    <Text size="sm" color="dimmed" sx={{ fontStyle: 'italic' }}>
                        Our highest honor — awarded to a comic who wins a contest two years in a row.
                    </Text>
                    <Space h="lg" />
                    <Text className={classes.pobblebonkWinner}>Alex Shklar</Text>
                    <Badge color="yellow" variant="filled" mt={6}>2024 Recipient</Badge>
                    <Space h="sm" />
                    <Center>
                        <List size="sm" sx={{ textAlign: 'left', color: '#7a5800' }}>
                            <List.Item>Global Top Comic</List.Item>
                            <List.Item>Top Comic – Brooklyn</List.Item>
                        </List>
                    </Center>
                </div>
            </Container>

            {/* ── Divider: Pobblebonk → Yearly Awards ── */}
            <Divider
                className={classes.sectionDivider}
                label={<span className={classes.dividerLabel}>End of Year Awards</span>}
                labelPosition="center"
                size="xs"
            />

            <Center>
                <Text className={classes.sectionTitle}>End of Year Awards</Text>
            </Center>

            {/* ══════════════════════════════════════════════════════════
                2025
            ══════════════════════════════════════════════════════════ */}
            <Container>
                <YearBanner year="2025" />
                <SimpleGrid
                    cols={2}
                    spacing="lg"
                    breakpoints={[{ maxWidth: '48rem', cols: 1, spacing: 'md' }]}
                >
                    {/* Comics / Regional */}
                    <Stack spacing="md">
                        <AwardEntry
                            category="Global Top Comic"
                            borderColor={COLORS.globalComic}
                            iconColor={COLORS.globalComic}
                            iconSize={32}
                            first="Xander Fraser — Jacksonville, FL"
                            second="Atul Patel, Minneapolis, MN"
                            third="Gage Aurora, Coconut Creek, FL"
                        />
                        <AwardEntry
                            category="Hardest Working Comic"
                            borderColor={COLORS.regional}
                            iconColor={COLORS.regional}
                            iconSize={22}
                            first="Xander Fraser — Jacksonville, FL"
                        />
                        <AwardEntry
                            category="Top Comic – Brooklyn"
                            borderColor={COLORS.regional}
                            iconColor={COLORS.regional}
                            iconSize={22}
                            first="Max Kaczor — Brooklyn, NY"
                            second="Charly Esturilho, Brooklyn, NY"
                            third="n/a"
                        />
                        <AwardEntry
                            category="Top Comic – Miami"
                            borderColor={COLORS.regional}
                            iconColor={COLORS.regional}
                            iconSize={22}
                            first="n/a"
                            second="n/a"
                            third="n/a"
                        />
                    </Stack>

                    {/* Fans / Mics */}
                    <Stack spacing="md">
                        <AwardEntry
                            category="Global Top Fan"
                            borderColor={COLORS.fan}
                            iconColor={COLORS.fan}
                            iconSize={32}
                            first="Andy Volosin — Miami, FL"
                            second="Gerardo Mondragon, San Bernardino, CA"
                            third="Daniel Martin, Gaithersburg, MD"
                        />
                        <AwardEntry
                            category="Global Top Mic"
                            borderColor={COLORS.mic}
                            iconColor={COLORS.mic}
                            iconSize={22}
                            first="Enoch's Variety Mic — NYC (contributor: Max Kaczor)"
                            second="Shantytown, Jacksonville, FL (contributor: Vanessa Scaduto)"
                            third="Voo Swar, Jacksonville, FL (contributor: Vanessa Scaduto)"
                        />
                    </Stack>
                </SimpleGrid>
            </Container>

            {/* ══════════════════════════════════════════════════════════
                2024
            ══════════════════════════════════════════════════════════ */}
            <Container>
                <YearBanner year="2024" />
                <SimpleGrid
                    cols={2}
                    spacing="lg"
                    breakpoints={[{ maxWidth: '48rem', cols: 1, spacing: 'md' }]}
                >
                    <Stack spacing="md">
                        <AwardEntry
                            category="Global Top Comic"
                            borderColor={COLORS.globalComic}
                            iconColor={COLORS.globalComic}
                            iconSize={32}
                            first="Alex Shklar — Brooklyn, NY"
                            second="Max Kaczor, Brooklyn, NY"
                            third="Charly Esturilho, Buffalo, NY"
                        />
                        <AwardEntry
                            category="Hardest Working Comic"
                            borderColor={COLORS.regional}
                            iconColor={COLORS.regional}
                            iconSize={22}
                            first="Alex Shklar — Brooklyn, NY"
                        />
                        <AwardEntry
                            category="Top Comic – Brooklyn"
                            borderColor={COLORS.regional}
                            iconColor={COLORS.regional}
                            iconSize={22}
                            first="Alex Shklar — Brooklyn, NY"
                            second="Max Kaczor, Brooklyn, NY"
                            third="Charly Esturilho, Brooklyn, NY"
                        />
                        <AwardEntry
                            category="Top Comic – Miami"
                            borderColor={COLORS.regional}
                            iconColor={COLORS.regional}
                            iconSize={22}
                            first="Sai Montilla — Miami, FL"
                            second="Eddy Pierre, Miami, FL"
                            third="J.P. Kauer, Miami, FL"
                        />
                    </Stack>

                    <Stack spacing="md">
                        <AwardEntry
                            category="Global Top Fan"
                            borderColor={COLORS.fan}
                            iconColor={COLORS.fan}
                            iconSize={32}
                            first="Lauren Jes — Bloomfield, MI"
                            second="James Buck, Byron Center, MI"
                            third="Gideon Moyo, Cleveland, OH"
                        />
                        <AwardEntry
                            category="Global Top Mic"
                            borderColor={COLORS.mic}
                            iconColor={COLORS.mic}
                            iconSize={22}
                            first="The FUN Mic with Alex Shklar — Brooklyn, NY"
                            second="Union Beer Store, Miami, FL (host: Connie Fernandez)"
                            third="QED Wednesday Late, Astoria, NY (contributor: Max Kaczor)"
                        />
                    </Stack>
                </SimpleGrid>
            </Container>

            {/* ══════════════════════════════════════════════════════════
                2023
            ══════════════════════════════════════════════════════════ */}
            <Container>
                <YearBanner year="2023" subtitle='1st year known as "Comedy Seen"' />
                <SimpleGrid
                    cols={2}
                    spacing="lg"
                    breakpoints={[{ maxWidth: '48rem', cols: 1, spacing: 'md' }]}
                >
                    <Stack spacing="md">
                        <AwardEntry
                            category="Global Top Comic"
                            borderColor={COLORS.globalComic}
                            iconColor={COLORS.globalComic}
                            iconSize={32}
                            first="Alex Shklar — Brooklyn, NY"
                            second="Max Kaczor, Brooklyn, NY"
                            third="Cody Z, Buffalo, NY"
                        />
                        <AwardEntry
                            category="Hardest Working Comic"
                            borderColor={COLORS.regional}
                            iconColor={COLORS.regional}
                            iconSize={22}
                            first="Alex Shklar — Brooklyn, NY"
                        />
                        <AwardEntry
                            category="Top Comic – Brooklyn"
                            borderColor={COLORS.regional}
                            iconColor={COLORS.regional}
                            iconSize={22}
                            first="Alex Shklar — Brooklyn, NY"
                            second="Max Kaczor, Brooklyn, NY"
                            third="Jose Bedoy, Brooklyn, NY"
                        />
                    </Stack>

                    <Stack spacing="md">
                        <AwardEntry
                            category="Global Top Fan"
                            borderColor={COLORS.fan}
                            iconColor={COLORS.fan}
                            iconSize={32}
                            first="Evan Daniels — Brooklyn, NY"
                            second="Leandro Roldan, Miami, FL"
                            third="Cristina Sarria, Nashville, TN"
                        />
                        <AwardEntry
                            category="Global Top Mic"
                            borderColor={COLORS.mic}
                            iconColor={COLORS.mic}
                            iconSize={22}
                            first="The FUN Mic with Alex Shklar — Brooklyn, NY"
                            second="Late Night Mic (Noah Miller), Brooklyn, NY"
                            third="Freddy's Wednesday Mic, Brooklyn, NY"
                        />
                    </Stack>
                </SimpleGrid>
            </Container>

            {/* ══════════════════════════════════════════════════════════
                2022
            ══════════════════════════════════════════════════════════ */}
            <Container>
                <YearBanner year="2022" subtitle='App was known as "Pobblebonk"' />
                <SimpleGrid
                    cols={2}
                    spacing="lg"
                    breakpoints={[{ maxWidth: '48rem', cols: 1, spacing: 'md' }]}
                >
                    <Stack spacing="md">
                        <AwardEntry
                            category="Global Top Comic"
                            borderColor={COLORS.globalComic}
                            iconColor={COLORS.globalComic}
                            iconSize={32}
                            first="Alex Shklar — Brooklyn, NY"
                            second="Cody Z, Orlando, FL"
                            third="Max Kaczor, Buffalo, NY"
                        />
                        <AwardEntry
                            category="Hardest Working Comic"
                            borderColor={COLORS.regional}
                            iconColor={COLORS.regional}
                            iconSize={22}
                            first="Cody Z — Orlando, FL"
                        />
                    </Stack>

                    <Stack spacing="md">
                        <AwardEntry
                            category="Global Top Mic"
                            borderColor={COLORS.mic}
                            iconColor={COLORS.mic}
                            iconSize={32}
                            first="The FUN Mic with Alex Shklar — Brooklyn, NY"
                            second="Monday Night Comedy Mic at Grumpy's, Orlando, FL (host: Ike Rafferty)"
                            third="Dane Curley's Underground Comedy, Brooklyn, NY"
                        />
                    </Stack>
                </SimpleGrid>
            </Container>

            <Space h="xl" />
            <Footer links={links} />
        </body>
    )
}

export default HOF